import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {interval, Subject} from 'rxjs';
import {startWith, takeUntil} from 'rxjs/operators';
import {ReportAdminService} from '../../services/api/report-admin.service';
import {AdminNavigationService} from '../../services/admin-navigation.service';
import {AccountAdminDto} from '../../dtos/accountAdmin.dto';
import {DateFormat, Dates, LoadingState, Title} from 'common';
import {BaseComponent} from '../base.component';
import {RecentAccountPaymentsReportDataViewModel} from '../../viewmodels/recentAccountPaymentsReportDataViewModel';
import {AdminReportGroupFilter} from '../../viewmodels/adminReportGroupFilter';
import {
    RecentAccountPaymentsReportAccountDataViewModel
} from '../../viewmodels/recentAccountPaymentsReportAccountDataViewModel';

@Component({
    selector: 'app-report',
    templateUrl: './recent-account-payments-report.component.html',
    styleUrls: ['./recent-account-payments-report.component.scss']
})

export class RecentAccountPaymentsReportComponent extends BaseComponent implements OnInit {
    public readonly allTransactionsDataSource: MatTableDataSource<RecentAccountPaymentsReportDataViewModel>;
    public readonly cancelledSubscriptionsDataSource: MatTableDataSource<RecentAccountPaymentsReportAccountDataViewModel>;

    public readonly churnDateRanges: typeof ChurnRateDateRange = ChurnRateDateRange;
    public readonly reportTypes: typeof ReportType = ReportType;
    public readonly quarters: typeof Quarters = Quarters;
    public readonly twelveMonthsAgo: number = new Date().setFullYear(new Date().getFullYear() - 1);
    public readonly sixMonthsAgo: number = new Date().setMonth(new Date().getMonth() - 6);

    public allTransactionsColumns: Map<string, Title>;
    public cancelledSubscriptionsColumns: Map<string, Title>;

    public loadingState: LoadingState;
    public loadingProgress: number;
    public loadingStatus: string;
    public dateCreated: string;
    public fileName: string;
    public allTransactionsViewModel: RecentAccountPaymentsReportDataViewModel;
    public allTransactionsFilter: AdminReportGroupFilter;
    public cancelledSubscriptionsViewModel: RecentAccountPaymentsReportAccountDataViewModel;
    public cancelledSubscriptionsFilter: AdminReportGroupFilter;
    private reportFinished: Subject<void>;

    public reportType: ReportType;
    public churnRateOption: ChurnRateDateRange;
    public customQuarter: Quarters;
    public selectedCustomYear: number;

    private fromDate: Date;
    private toDate: Date;

    private cancelledSubscriptionsReportData: RecentAccountPaymentsReportAccountDataViewModel[];

    private accountFirstPaymentMap = new Map();

    @ViewChild(MatSort)
    public sort: MatSort;

    constructor(private route: ActivatedRoute, private reportAdminService: ReportAdminService, private adminNavigation: AdminNavigationService) {
        super();
        this.allTransactionsDataSource = new MatTableDataSource([]);
        this.cancelledSubscriptionsDataSource = new MatTableDataSource([]);

        this.allTransactionsColumns = new Map<string, string>();
        this.allTransactionsColumns.set('position', 'No');
        this.allTransactionsColumns.set('fullName', 'Name');
        this.allTransactionsColumns.set('email', 'Mail');
        this.allTransactionsColumns.set('subscription', 'Subscription');
        this.allTransactionsColumns.set('subscriptionPeriodUnit', 'subscription Period');
        this.allTransactionsColumns.set('subscriptionSource', 'Subscription Source');
        this.allTransactionsColumns.set('paymentDate', 'Payment Date');
        this.allTransactionsColumns.set('amount', 'Amount');
        this.allTransactionsColumns.set('discount', 'Discount');
        this.allTransactionsColumns.set('renewalStatus', 'Renewable');
        this.allTransactionsColumns.set('active', 'Active');

        this.cancelledSubscriptionsColumns = new Map<string, string>();
        this.cancelledSubscriptionsColumns.set('position', 'No');
        this.cancelledSubscriptionsColumns.set('fullName', 'Name');
        this.cancelledSubscriptionsColumns.set('email', 'Mail');
        this.cancelledSubscriptionsColumns.set('lastSubscription', ' Last Subscription');
        this.cancelledSubscriptionsColumns.set('lastSubscriptionPeriodUnit', 'Last Subscription Period');
        this.cancelledSubscriptionsColumns.set('lastSubscriptionSource', 'Last Subscription Source');
        this.cancelledSubscriptionsColumns.set('firstPaymentDate', 'First Payment');
        this.cancelledSubscriptionsColumns.set('lastPaymentDate', 'Last Payment ');
        this.cancelledSubscriptionsColumns.set('expirationDate', 'Expiration Date');
        this.cancelledSubscriptionsColumns.set('amount', 'Amount');
        this.cancelledSubscriptionsColumns.set('discount', 'Discount');
        this.cancelledSubscriptionsColumns.set('renewalStatus', 'Renewable');
        this.cancelledSubscriptionsColumns.set('active', 'Active');

        this.allTransactionsViewModel = new RecentAccountPaymentsReportDataViewModel();
        this.cancelledSubscriptionsViewModel = new RecentAccountPaymentsReportAccountDataViewModel();
        this.reportFinished = new Subject<void>();

        this.reportType = ReportType.ALL_TRANSACTIONS;
        this.churnRateOption = ChurnRateDateRange.FOREVER;

        this.fromDate = new Date(
            new Date().getFullYear(),
            new Date().getMonth() - 3,
            new Date().getDate()
        );
        this.toDate = new Date();

        this.allTransactionsFilter = new AdminReportGroupFilter();
        this.allTransactionsFilter.supportsRenewable = true;
        this.allTransactionsFilter.supportsActive = true;
        this.allTransactionsFilter.supportsQuery = true;
        this.allTransactionsFilter.supportsSubscriptionPlanTypes = true;
        this.allTransactionsFilter.supportsSubscriptionPeriodUnits = true;
        this.allTransactionsFilter.supportsSubscriptionSources = true;
        this.allTransactionsFilter.supportsCloudAdminSubscriptionSource = false;
        this.allTransactionsFilter.supportSubscriptionCustomPeriodUnit = false;

        this.cancelledSubscriptionsFilter = new AdminReportGroupFilter();
        this.cancelledSubscriptionsFilter.supportsRenewable = false; // No reason for this
        this.cancelledSubscriptionsFilter.supportsActive = true;
        this.cancelledSubscriptionsFilter.supportsQuery = true;
        this.cancelledSubscriptionsFilter.supportsSubscriptionPlanTypes = true;
        this.cancelledSubscriptionsFilter.supportsSubscriptionPeriodUnits = true;
        this.cancelledSubscriptionsFilter.supportsSubscriptionSources = true;
        this.cancelledSubscriptionsFilter.supportsCloudAdminSubscriptionSource = false;
        this.cancelledSubscriptionsFilter.supportSubscriptionCustomPeriodUnit = false;
    }

    ngOnInit(): void {
        this.analyticsSwitchPage('Admin-RecentAccountPaymentsReport', '', '/admin/recentAccountPaymentsReport');

        // Initiate report generation and periodically check status
        this.loadingState = LoadingState.LOADING;

        const pollingIntervalMs = 2500;
        interval(pollingIntervalMs).pipe(
            startWith(0), // start immediately
            takeUntil(this.reportFinished),
            takeUntil(this.destroySubject),
        ).subscribe(_avoid => this.checkReportStatus());
    }

    public onReportTypeSelected(reportType: ReportType): void {
        this.reportType = reportType;
    }

    public onChurnRateYearSelected(year: number): void {
        this.selectedCustomYear = year;

        this.onQuarterSelected(this.customQuarter);
    }

    public onQuarterSelected(quarter: Quarters): void {
         this.customQuarter = quarter;

         if (quarter === Quarters.Q1) {
             this.fromDate = new Date(this.selectedCustomYear, 0, 1);
             this.toDate = new Date(this.selectedCustomYear, 2, this.getLastDayOfMonth(this.selectedCustomYear, 2), 23, 59, 59, 999);
         } else if (quarter === Quarters.Q2) {
             this.fromDate = new Date(this.selectedCustomYear, 3, 1);
             this.toDate = new Date(this.selectedCustomYear, 5, this.getLastDayOfMonth(this.selectedCustomYear, 5), 23, 59, 59, 999);
         } else if (quarter === Quarters.Q3) {
             this.fromDate = new Date(this.selectedCustomYear, 6, 1);
             this.toDate = new Date(this.selectedCustomYear, 8, this.getLastDayOfMonth(this.selectedCustomYear, 8), 23, 59, 59, 999);
         } else {
             this.fromDate = new Date(this.selectedCustomYear, 9, 1);
             this.toDate = new Date(this.selectedCustomYear, 11, this.getLastDayOfMonth(this.selectedCustomYear, 11), 23, 59, 59, 999);
         }

         this.updateCancelledSubscriptionsDateRange(ChurnRateDateRange.CUSTOM_DATE);
    }

    private getLastDayOfMonth(year: number, month: number): number {
        return new Date(year, month + 1, 0).getDate();
    }

    public onChurnRateDateRangeSelected(dateRange: ChurnRateDateRange): void {
        this.churnRateOption = dateRange;

        if (dateRange === ChurnRateDateRange.CUSTOM_DATE) {
            this.selectedCustomYear = new Date().getFullYear();
            this.onQuarterSelected(Quarters.Q1);
        }

        this.updateCancelledSubscriptionsDateRange(dateRange);
    }

    private updateCancelledSubscriptionsDateRange(dateRange: ChurnRateDateRange): void {
        if (dateRange === ChurnRateDateRange.FOREVER) {
            this.cancelledSubscriptionsDataSource.data = this.cancelledSubscriptionsReportData;
        } else if (dateRange === ChurnRateDateRange.LAST_SIX_MONTHS) {
            this.cancelledSubscriptionsDataSource.data =
                this.cancelledSubscriptionsReportData
                    .filter(cancelledAccountRow => cancelledAccountRow.firstPaymentDate > this.sixMonthsAgo);
        } else if (dateRange === ChurnRateDateRange.LAST_YEAR) {
            this.cancelledSubscriptionsDataSource.data =
                this.cancelledSubscriptionsReportData
                    .filter(cancelledAccountRow => cancelledAccountRow.firstPaymentDate > this.twelveMonthsAgo);
        } else {
            // Custom data
            this.cancelledSubscriptionsDataSource.data =
                this.cancelledSubscriptionsReportData.filter(cancelledSub => cancelledSub.firstPaymentDate > this.fromDate.getTime() &&
                    cancelledSub.firstPaymentDate < this.toDate.getTime());
        }
    }

    public getAccountsCancelledAndBoughtASubscription(): string {
        return this.cancelledSubscriptionsDataSource.data.length.toString();
    }

    public getTotalAccounts(): string {
        if (this.reportType === ReportType.ALL_TRANSACTIONS) {
            return this.accountFirstPaymentMap.size.toString();
        }

        if (this.churnRateOption === ChurnRateDateRange.FOREVER) {
            return this.accountFirstPaymentMap.size.toString();
        } else {
            let totalAccounts = 0;
            for (const [email, firstPaymentDate] of this.accountFirstPaymentMap.entries()) {
                if ((this.churnRateOption === ChurnRateDateRange.LAST_YEAR && firstPaymentDate >= this.twelveMonthsAgo) ||
                    (this.churnRateOption === ChurnRateDateRange.LAST_SIX_MONTHS && firstPaymentDate >= this.sixMonthsAgo) ||
                    (this.churnRateOption === ChurnRateDateRange.CUSTOM_DATE &&
                        firstPaymentDate >= this.fromDate.getTime() &&
                        firstPaymentDate <= this.toDate.getTime())) {
                    totalAccounts++;
                }
            }
            return totalAccounts.toString();
        }
    }

    public getChurnRate(): string {
        const cancelledAccounts = Number(this.getAccountsCancelledAndBoughtASubscription());
        const totalAccounts = Number(this.getTotalAccounts());
        if (totalAccounts === 0) {
            return 'Not Applicable.';
        }

        const churnRate = (cancelledAccounts / totalAccounts) * 100;

        return churnRate.toFixed(2) + ' %';
    }

    private checkReportStatus(): void {
        this.reportAdminService.getRecentAccountPayments()
            .pipe(takeUntil(this.destroySubject))
            .subscribe(report => {
                const reportFinished = report.reportStatus.dateCreated !== 0;
                if (!reportFinished) {
                    this.loadingProgress = report.reportStatus.progress;
                    this.loadingStatus = report.reportStatus.progress.toString();
                    return;
                }
                this.reportFinished.next();
                this.reportFinished.complete();

                this.allTransactionsDataSource.data = this.allTransactionsViewModel.from(report.subscriptionPayments);
                this.cancelledSubscriptionsReportData = this.cancelledSubscriptionsViewModel.from(report.subscriptionPayments);
                this.cancelledSubscriptionsDataSource.data = this.cancelledSubscriptionsReportData;

                for (let i = 0; i < report.subscriptionPayments.length; i++) {
                    const subPaymentDto = report.subscriptionPayments[i];
                    let firstPaymentDate = 0;
                    subPaymentDto.subscriptions
                        .flatMap(sub => sub.payments)
                        .forEach(payment => {
                            if (firstPaymentDate === 0) {
                                firstPaymentDate = payment.paymentDate;
                            } else if (payment.paymentDate < firstPaymentDate) {
                                firstPaymentDate = payment.paymentDate;
                            }
                        });
                    this.accountFirstPaymentMap.set(subPaymentDto.email, firstPaymentDate);
                }

                const date = new Date(report.reportStatus.dateCreated);
                this.dateCreated = Dates.format(date, DateFormat.DATE_TIME_LONG);
                this.fileName = `account_activity_report_${Dates.format(date, DateFormat.DATE_TIME_CONDENSED)}`;

                this.loadingState = LoadingState.LOADED;
            }, (error) => {
                this.errorHandler.handle(`Could not create report. ${error.statusText} (${error.status}`);
                this.notification.info(`Could not create report. ${error.statusText} (${error.status}`);
                this.loadingState = LoadingState.ERROR;
            });
    }
}

enum ChurnRateDateRange {
    FOREVER,
    LAST_YEAR,
    LAST_SIX_MONTHS,
    CUSTOM_DATE
}

enum ReportType {
    ALL_TRANSACTIONS,
    CANCELLED_SUBSCRIPTIONS,
}

enum Quarters {
    Q1,
    Q2,
    Q3,
    Q4
}
